import { create } from 'zustand'

export type SettingsSection =
  | 'general'
  | 'account'
  | 'restaurant'
  | 'integrations'

/** Where the venue's Google place is set, inside the restaurant section. */
export const GOOGLE_LOCATION_ANCHOR = 'settings-google-location'

const SECTIONS: SettingsSection[] = ['general', 'account', 'restaurant']
const REOPEN_KEY = 'settings-reopen-section'

/**
 * Opens the modal on `section` once the page has loaded again, for an action
 * taken in it that reloads the page: switching to another venue
 * (`switchRestaurant`) would otherwise close the dialog it was done in. Per
 * tab, so no other tab opens it.
 */
export const reopenAfterReload = (section: SettingsSection) => {
  try {
    sessionStorage.setItem(REOPEN_KEY, section)
  } catch {
    // Storage blocked: the switch still happens, the dialog just stays shut.
  }
}

// Read as the store is created, which a browser that blocks storage refuses
// with a throw; that must not stop every page importing settings from loading.
const takeReopenSection = (): SettingsSection | null => {
  try {
    const section = sessionStorage.getItem(REOPEN_KEY)
    sessionStorage.removeItem(REOPEN_KEY)
    return SECTIONS.find(entry => entry === section) ?? null
  } catch {
    return null
  }
}

interface SettingsModalState {
  /** The section on screen, or null while the modal is closed. */
  section: SettingsSection | null
  /** A block to bring into view once the section is open. */
  anchor: string | null
  open: (section?: SettingsSection, anchor?: string) => void
  close: () => void
}

/**
 * A store rather than local state because more than one place opens the
 * modal, the account menu and the venue facts on the website template, and
 * each wants it on a different section.
 */
export const useSettingsModal = create<SettingsModalState>(set => ({
  section: takeReopenSection(),
  anchor: null,
  open: (section = 'general', anchor) =>
    set({ section, anchor: anchor ?? null }),
  close: () => set({ section: null, anchor: null })
}))
